import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Handshake, Search, AlertTriangle, RotateCcw, X, TrendingUp, Shapes } from "lucide-react";
import { AppShell } from "@/components/layout/AppShell";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ConvenioCard } from "@/components/convenios/ConvenioCard";
import { ConvenioDetailSheet } from "@/components/convenios/ConvenioDetailSheet";
import { conveniosMarcoService, type ConvenioMarco } from "@/services/convenios-marco.service";

export const Route = createFileRoute("/convenios-marco")({
  head: () => ({
    meta: [
      { title: "Convenio Marco — LicitaPro" },
      {
        name: "description",
        content:
          "Convenios marco vigentes de Mercado Público: rubro, proveedores adjudicados y cuándo vence cada contrato.",
      },
    ],
  }),
  component: Page,
});

const VENTANAS = [
  { value: "todos", label: "Todos los convenios" },
  { value: "vigentes", label: "Solo vigentes" },
  { value: "vencidos", label: "Solo vencidos" },
  { value: "6", label: "Vencen en 6 meses" },
  { value: "12", label: "Vencen en 12 meses" },
];

const ORDENES = [
  { value: "vigencia", label: "Próximos a vencer" },
  { value: "monto", label: "Mayor monto transado" },
  { value: "compras", label: "Más compras" },
  { value: "nombre", label: "Nombre (A-Z)" },
];

const PAGE_SIZE = 24;

function Page() {
  const [q, setQ] = useState("");
  const [ventana, setVentana] = useState("todos");
  const [orden, setOrden] = useState("vigencia");
  const [pagina, setPagina] = useState(0);
  const [seleccionado, setSeleccionado] = useState<string | null>(null);

  const venceEnMeses = ventana === "6" || ventana === "12" ? Number(ventana) : undefined;

  const convenios = useQuery({
    queryKey: ["convenios-marco", q, venceEnMeses ?? "todos"],
    queryFn: () => conveniosMarcoService.list({ q: q.trim() || undefined, venceEnMeses }),
  });

  // Sólo para los chequeos de "hay resultados" del render; el useMemo depende
  // de `convenios.data` y no de esta variable, porque el `?? []` crea un array
  // nuevo en cada render y dejaría al memo sin memorizar nada.
  const lista: ConvenioMarco[] = convenios.data ?? [];

  const filtrados = useMemo(() => {
    let out: ConvenioMarco[] = convenios.data ?? [];
    if (ventana === "vigentes") out = out.filter((c) => (c.diasParaVencer ?? 1) >= 0);
    if (ventana === "vencidos") out = out.filter((c) => (c.diasParaVencer ?? 0) < 0);
    const cmp: Record<string, (a: ConvenioMarco, b: ConvenioMarco) => number> = {
      vigencia: (a, b) => (a.diasParaVencer ?? 1e9) - (b.diasParaVencer ?? 1e9),
      monto: (a, b) => b.montoCompras - a.montoCompras,
      compras: (a, b) => b.compras - a.compras,
      nombre: (a, b) => a.nombre.localeCompare(b.nombre, "es-CL"),
    };
    return [...out].sort(cmp[orden] ?? cmp.vigencia);
  }, [convenios.data, ventana, orden]);

  const totalPaginas = Math.max(1, Math.ceil(filtrados.length / PAGE_SIZE));
  const paginaActual = Math.min(pagina, totalPaginas - 1);
  const visibles = filtrados.slice(paginaActual * PAGE_SIZE, (paginaActual + 1) * PAGE_SIZE);

  /** Alto impacto: tercio superior por monto transado dentro de la página visible. */
  const { altos, otros } = useMemo(() => {
    if (visibles.length === 0) return { altos: [], otros: [] };
    const porMonto = [...visibles].sort((a, b) => b.montoCompras - a.montoCompras);
    const corte = Math.max(1, Math.ceil(porMonto.length / 3));
    const claveAlta = new Set(
      porMonto.slice(0, corte).filter((c) => c.montoCompras > 0).map((c) => c.codigo),
    );
    return {
      altos: visibles.filter((c) => claveAlta.has(c.codigo)),
      otros: visibles.filter((c) => !claveAlta.has(c.codigo)),
    };
  }, [visibles]);

  const hayFiltros = q.trim() !== "" || ventana !== "todos" || orden !== "vigencia";
  const limpiar = () => {
    setQ("");
    setVentana("todos");
    setOrden("vigencia");
    setPagina(0);
  };

  return (
    <AppShell
      title="Convenio Marco"
      subtitle="Convenios vigentes, proveedores adjudicados y cuándo se vuelven a licitar"
    >
      <div className="flex flex-col gap-3 lg:flex-row lg:items-center">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            className="pl-9"
            placeholder="Buscar por rubro o código del convenio"
            value={q}
            onChange={(e) => {
              setQ(e.target.value);
              setPagina(0);
            }}
          />
        </div>
        <Select
          value={ventana}
          onValueChange={(v) => {
            setVentana(v);
            setPagina(0);
          }}
        >
          <SelectTrigger className="lg:w-56" aria-label="Filtrar por vigencia">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {VENTANAS.map((v) => (
              <SelectItem key={v.value} value={v.value}>
                {v.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={orden} onValueChange={setOrden}>
          <SelectTrigger className="lg:w-56" aria-label="Ordenar convenios">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {ORDENES.map((o) => (
              <SelectItem key={o.value} value={o.value}>
                {o.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {hayFiltros && (
          <Button variant="ghost" size="sm" onClick={limpiar} className="shrink-0">
            <X className="mr-1.5 size-4" />
            Limpiar
          </Button>
        )}
      </div>

      {convenios.isLoading && (
        <div className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {[0, 1, 2, 3, 4, 5].map((i) => (
            <Skeleton key={i} className="h-52 w-full rounded-xl" />
          ))}
        </div>
      )}

      {convenios.isError && (
        <div className="mt-5 flex flex-wrap items-center gap-3 rounded-xl border border-destructive/30 bg-destructive/10 p-4 text-sm text-destructive">
          <AlertTriangle className="size-4" />
          No se pudieron cargar los convenios marco.
          <Button size="sm" variant="outline" onClick={() => convenios.refetch()}>
            <RotateCcw className="mr-1.5 size-4" />
            Reintentar
          </Button>
        </div>
      )}

      {!convenios.isLoading && !convenios.isError && lista.length === 0 && (
        <div className="mt-8 rounded-2xl border border-dashed border-border p-10 text-center">
          <Handshake className="mx-auto size-10 text-muted-foreground" />
          <h3 className="mt-3 font-display text-lg font-semibold">Aún no hay convenios marco</h3>
          <p className="mt-1 text-sm text-muted-foreground">
            La sincronización programada los va consolidando desde Mercado Público a medida que
            aparecen compras de Convenio Marco.
          </p>
        </div>
      )}

      {!convenios.isLoading && !convenios.isError && lista.length > 0 && (
        <>
          <p className="mt-4 text-sm text-muted-foreground">
            {filtrados.length.toLocaleString("es-CL")}{" "}
            {filtrados.length === 1 ? "convenio" : "convenios"}
            {filtrados.length > PAGE_SIZE ? ` · página ${paginaActual + 1} de ${totalPaginas}` : ""}
          </p>

          {filtrados.length === 0 ? (
            <div className="mt-4 rounded-xl border border-dashed border-border p-10 text-center">
              <h3 className="font-display text-base font-semibold">Sin resultados</h3>
              <p className="mt-1 text-sm text-muted-foreground">
                Ningún convenio coincide con los filtros seleccionados.
              </p>
              <Button variant="outline" size="sm" className="mt-4" onClick={limpiar}>
                Limpiar filtros
              </Button>
            </div>
          ) : (
            <>
              {altos.length > 0 && (
                <section className="mt-5">
                  <h2 className="flex items-center gap-2 text-sm font-semibold text-foreground">
                    <TrendingUp className="size-4 text-success" />
                    Alto impacto económico / uso frecuente
                  </h2>
                  <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                    {altos.map((c) => (
                      <ConvenioCard
                        key={c.codigo}
                        convenio={c}
                        destacado
                        onSelect={() => setSeleccionado(c.codigo)}
                      />
                    ))}
                  </div>
                </section>
              )}

              {otros.length > 0 && (
                <section className="mt-8">
                  <h2 className="flex items-center gap-2 text-sm font-semibold text-foreground">
                    <Shapes className="size-4 text-muted-foreground" />
                    Impacto medio y otros convenios
                  </h2>
                  <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                    {otros.map((c) => (
                      <ConvenioCard key={c.codigo} convenio={c} onSelect={() => setSeleccionado(c.codigo)} />
                    ))}
                  </div>
                </section>
              )}

              {totalPaginas > 1 && (
                <div className="mt-6 flex flex-wrap items-center justify-between gap-3">
                  <span className="text-xs text-muted-foreground">
                    Página {paginaActual + 1} de {totalPaginas}
                  </span>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={paginaActual === 0}
                      onClick={() => setPagina(paginaActual - 1)}
                    >
                      Anterior
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={paginaActual >= totalPaginas - 1}
                      onClick={() => setPagina(paginaActual + 1)}
                    >
                      Siguiente
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </>
      )}

      <ConvenioDetailSheet
        codigo={seleccionado}
        onOpenChange={(open) => !open && setSeleccionado(null)}
      />
    </AppShell>
  );
}
